import { Application } from '@platform/client';
import { ProfessionalApplicationStatusEnum } from '@platform/client/schemas';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { F5Submission } from '../final-details/f5/F5Submission';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { F5WorkExperience } from '../program/f5/F5WorkExperience';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { ApplicationApproved } from '../result/shared/ApplicationApproved';
import { ApplicationRejected } from '../result/shared/ApplicationRejected';
import { ApplicationUnderReview } from '../result/shared/ApplicationUnderReview';
import { ApplicationWaitlisted } from '../result/shared/ApplicationWaitlisted';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';

enum F5ApplicationSteps {
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',
  F5_EXPERIENCE_SELECTION = 'F5_EXPERIENCE_SELECTION',

  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',

  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',

  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
}

const F5ApplicationStep: Record<F5ApplicationSteps, Step> = {
  [F5ApplicationSteps.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/',
  },
  [F5ApplicationSteps.F5_EXPERIENCE_SELECTION]: {
    progress: 30,
    component: F5WorkExperience,
    url: '/f5-experience-selection',
  },

  [F5ApplicationSteps.LOCATION_SELECTION]: {
    progress: 30,
    component: LocationSelection,
    url: '/location-selection',
  },
  [F5ApplicationSteps.RESUME_SELECTION]: {
    progress: 30,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [F5ApplicationSteps.EXPERIENCE_SELECTION]: {
    progress: 170,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [F5ApplicationSteps.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [F5ApplicationSteps.SKILLS_SELECTION]: {
    progress: 235,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [F5ApplicationSteps.SKILL_IMPACT_SELECTION]: {
    progress: 275,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [F5ApplicationSteps.EXPERIENCE_HOPE_SELECTION]: {
    progress: 340,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [F5ApplicationSteps.REFERRAL_SELECTION]: {
    progress: 385,
    component: F5Submission,
    url: '/referral-selection',
  },
} as const;

export const F5ApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const goToNextStep = () => {
    const steps = Object.values(F5ApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return F5ApplicationStep[step as F5ApplicationSteps].url === location;
    });
    const nextStep = currentIndex < steps.length - 1 ? steps[currentIndex + 1] : location;
    const nextUrl = F5ApplicationStep[nextStep as F5ApplicationSteps].url;
    navigate(nextUrl);
    return nextStep as F5ApplicationSteps;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(F5ApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => F5ApplicationStep[step as F5ApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?partner=f5';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = F5ApplicationStep[previousStep as F5ApplicationSteps].url;
    navigate(previousUrl);
    return previousStep as F5ApplicationSteps;
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.waitlisted) {
    return <ApplicationWaitlisted programName='Volunteer Sprint' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.rejected) {
    return <ApplicationRejected programName='Volunteer Sprint' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.approved) {
    return <ApplicationApproved />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.pending) {
    return <ApplicationUnderReview />;
  }

  return (
    <Switch>
      {Object.entries(F5ApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
